import { useCallback, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import { useIsMobile } from '@/app/hooks/useIsMobile';
import { AuthenticationActions } from '@/app/slices';
import type { NavMode } from '@/app/slices/navigation';
import { PreferencesActions } from '@/app/slices/preferences';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { recordModuleVisit } from '@/hooks/useRecentModules';
import { useLogout } from '@/services/auth';

import { CollapsibleNavItem } from '../collapsible-nav-item';
import { NavItem, useNavigationItems } from './navigation-items';

import './NavigationPanel.css';

type NavigationPanelProps = {
  /**
   * full = labels, rail = icons only (desktop), closed = hidden
   */
  mode: NavMode;
  onModeChange: (mode: NavMode) => void;
};

/**
 * NavigationPanel
 *
 * Renders a mobile/desktop-responsive drawer with an overlay on mobile.
 * Uses Framer Motion for animations, CollapsibleNavItem for nested menu items.
 */
const FULL_WIDTH = 300;
const RAIL_WIDTH = 56;
const EASE = [0.32, 0.72, 0, 1] as const;

export const NavigationPanel: React.FC<NavigationPanelProps> = ({
  mode,
  onModeChange,
}) => {
  const dispatch = useDispatch();
  const [logout] = useLogout();
  const isMobile = useIsMobile();

  const handleSignOut = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({
        type: AuthenticationActions.UPDATE_ME,
        me: undefined,
      });

      window.location.assign('/');
    }
  }, [logout, dispatch]);

  const navItems = useNavigationItems();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const visibleNavItems = navItems.filter((item) => {
    if (
      item.divider ||
      item.path ||
      item.isPreferencesButton ||
      item.isSignOutButton
    ) {
      return true;
    }

    return item.children && item.children.length > 0;
  });

  /**
   * Called when the user clicks on a NavItem link
   */
  const handleNavItemClick = useCallback(() => {
    if (isMobile) {
      onModeChange('closed');
    }
  }, [isMobile, onModeChange]);

  /**
   * Recursively renders NavItems
   */
  const renderNavItem = (item: NavItem, idx: number): JSX.Element | null => {
    if (item.divider) {
      return <li key={`divider-${idx}`} className="divider" />;
    }
    if (item.isPreferencesButton) {
      return (
        <button
          key={`pref-${idx}`}
          className="collapsible-nav-button"
          style={{ marginLeft: '0.9rem' }}
          onClick={() => {
            dispatch({ type: PreferencesActions.UPDATE_SHOW, show: true });
            if (isMobile) onModeChange('closed');
          }}
        >
          {item.label}
        </button>
      );
    }
    if (item.isSignOutButton) {
      return (
        <button
          key={`signout-${idx}`}
          className="collapsible-nav-button"
          style={{ marginLeft: '0.9rem' }}
          onClick={handleSignOut}
        >
          {item.label}
        </button>
      );
    }
    if (item.children && item.children.length > 0) {
      return (
        <CollapsibleNavItem
          key={`collapsible-${item.label}-${idx}`}
          label={item.label!}
          icon={item.icon}
          defaultOpen={openGroup === item.label}
        >
          {item.children.map((child, cIdx) => renderNavItem(child, cIdx))}
        </CollapsibleNavItem>
      );
    }
    if (item.path) {
      return (
        <NavLink
          key={`nav-${item.path}-${idx}`}
          className="nav-link"
          to={item.path}
          onClick={() => {
            if (item.path && item.label && item.path !== '/') {
              recordModuleVisit(item.path, item.label);
            }
            handleNavItemClick();
          }}
          end
        >
          {item.icon ? (
            <span className="inline-flex items-center gap-2.5">
              <item.icon className="size-[18px] shrink-0" />
              {item.label}
            </span>
          ) : (
            item.label
          )}
        </NavLink>
      );
    }
    return null;
  };

  const containsActivePath = (item: NavItem): boolean =>
    (!!item.path &&
      item.path !== '/' &&
      location.pathname.startsWith(item.path)) ||
    !!item.children?.some(containsActivePath);

  // Row heights mirror the full nav so items line up when toggling modes: a
  // 1.5rem text line plus .nav-link padding (1.1rem) for links, or li (.75rem)
  // + button (.6rem) padding for groups.
  const railItemClass = (active: boolean, isGroup = false) =>
    `flex w-10 ${isGroup ? 'h-[4.2rem]' : 'h-[3.7rem]'} items-center justify-center rounded-md border-0 bg-transparent text-[var(--text-color)] transition-colors hover:bg-[var(--surface-hover)] cursor-pointer ${
      active ? 'bg-[var(--surface-border)]!' : ''
    }`;

  const renderRailItem = (item: NavItem, idx: number): JSX.Element | null => {
    if (item.divider) {
      return (
        <li
          key={`divider-${idx}`}
          className="my-[0.5rem] w-8 border-t border-[var(--surface-border)]"
        />
      );
    }
    if (!item.icon || !item.label) return null;
    const Icon = item.icon;
    const trigger = item.path ? (
      <NavLink
        to={item.path}
        end
        aria-label={item.label}
        className={({ isActive }) => railItemClass(isActive)}
        onClick={() => {
          if (item.path && item.path !== '/') {
            recordModuleVisit(item.path, item.label!);
          }
        }}
      />
    ) : (
      <button
        type="button"
        aria-label={item.label}
        className={railItemClass(containsActivePath(item), true)}
        onClick={() => {
          setOpenGroup(item.label!);
          onModeChange('full');
        }}
      />
    );
    return (
      <li key={`rail-${item.label}-${idx}`}>
        <Tooltip>
          <TooltipTrigger render={trigger}>
            <Icon className="size-5" />
          </TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      </li>
    );
  };

  const isRail = !isMobile && mode === 'rail';
  const isOpen = isMobile ? mode === 'full' : mode !== 'closed';
  const width = isRail ? RAIL_WIDTH : FULL_WIDTH;
  const transition = { duration: 0.28, ease: EASE };

  return (
    <AnimatePresence initial={false}>
      {isOpen && isMobile && (
        <motion.div
          key="overlay"
          className="drawer-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
          onClick={() => onModeChange('closed')}
        />
      )}
      {isOpen && (
        <motion.aside
          key="drawer"
          className="app-drawer-motion"
          style={{ overflowX: 'hidden' }}
          initial={isMobile ? { x: '-100%' } : { width: 0 }}
          animate={isMobile ? { x: 0 } : { width }}
          exit={isMobile ? { x: '-100%' } : { width: 0 }}
          transition={transition}
        >
          <motion.div
            key={isRail ? 'rail' : 'full'}
            style={{ width: isMobile ? '100%' : width }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18, delay: 0.06 }}
          >
            {isRail ? (
              <nav aria-label="Main">
                <ul className="m-0 flex list-none flex-col items-center p-0">
                  {visibleNavItems.map((item, index) =>
                    renderRailItem(item, index),
                  )}
                </ul>
              </nav>
            ) : (
              <nav className="app-nav">
                <ul>
                  {visibleNavItems.map((item, index) =>
                    renderNavItem(item, index),
                  )}
                </ul>
              </nav>
            )}
          </motion.div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};
